define("ROOM4.CCAProductGallery.CCAProductGallery.DescriptionHelper", [
  "underscore",
  "jQuery",
  "ROOM4.CCAProductGallery.CCAProductGallery.SchemaHelper",
  "ROOM4.CCAProductGallery.DOMPurify",
], function (_, jQuery, SchemaHelper, DOMPurify) {
  "use strict";

  var ACCESSORY_DESCRIPTION_ALLOWED_TAGS = [
    "p",
    "br",
    "span",
    "div",
    "strong",
    "b",
    "em",
    "i",
    "h2",
    "h3",
    "h4",
    "ul",
    "ol",
    "li",
    "blockquote",
    "a",
  ];
  var ACCESSORY_DESCRIPTION_SANITIZE_CONFIG = {
    ALLOWED_TAGS: ACCESSORY_DESCRIPTION_ALLOWED_TAGS,
    ALLOWED_ATTR: ["href", "title"],
    ALLOW_ARIA_ATTR: false,
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?):\/\/|\/(?!\/)|#|\?|\.{1,2}\/|[^:/?#\s]+(?:[/?#]|$))/i,
    FORCE_BODY: true,
    RETURN_TRUSTED_TYPE: false,
  };
  var hasReportedUnsupportedSanitizer = false;

  function compactParts(parts) {
    return _.filter(parts, function (part) {
      return part !== null && part !== undefined && String(part).trim();
    }).map(function (part) {
      return String(part).trim();
    });
  }

  function parseDescription(description) {
    var parsedDescription;

    if (!description) {
      return [];
    }

    try {
      parsedDescription = JSON.parse(description);
    } catch (error) {
      return [];
    }

    return _.isArray(parsedDescription) ? parsedDescription : [];
  }

  function getFinish(entry) {
    return _.isArray(entry.finish) ? entry.finish : [];
  }

  function formatWheelCaption(entry) {
    var line1 = formatWheelProductName(entry);
    var line2 = "";

    if (entry.diameter || entry.width) {
      line2 = compactParts([entry.diameter, entry.width]).join("×");
    }

    if (
      entry.offset !== null &&
      entry.offset !== undefined &&
      String(entry.offset).trim()
    ) {
      line2 = compactParts([line2, String(entry.offset).trim() + "㎜"]).join(
        " "
      );
    }

    return {
      line1: line1,
      line2: line2,
    };
  }

  function formatTireCaption(entry) {
    return {
      line1: formatTireProductName(entry),
      line2: compactParts([entry.shortSize]).join(" "),
    };
  }

  function formatGalleryDescription(description) {
    return _.chain(parseDescription(description))
      .filter(function (entry) {
        return entry && entry.brand;
      })
      .map(function (entry) {
        if (entry.type === "wheel") {
          return formatWheelCaption(entry);
        }

        if (entry.type === "tire") {
          return formatTireCaption(entry);
        }

        return null;
      })
      .filter(function (entry) {
        return entry && (entry.line1 || entry.line2);
      })
      .value();
  }

  function formatWheelProductName(entry) {
    return compactParts([entry.brand, entry.model].concat(getFinish(entry))).join(
      " "
    );
  }

  function formatTireProductName(entry) {
    return compactParts([entry.brand, entry.model]).join(" ");
  }

  function uniqueValues(values) {
    var seen = {};

    return _.filter(values, function (value) {
      var key = String(value || "").toLowerCase();

      if (!value || seen[key]) {
        return false;
      }

      seen[key] = true;
      return true;
    });
  }

  function getSeoProductSummary(description) {
    var entries = parseDescription(description);
    var wheels = [];
    var tires = [];

    _.each(entries, function (entry) {
      var productName;

      if (!entry || !entry.brand) {
        return;
      }

      if (entry.type === "wheel") {
        productName = formatWheelProductName(entry);
        if (productName) {
          wheels.push(productName);
        }
      }

      if (entry.type === "tire") {
        productName = formatTireProductName(entry);
        if (productName) {
          tires.push(productName);
        }
      }
    });

    wheels = uniqueValues(wheels);
    tires = uniqueValues(tires);

    return {
      wheels: wheels.join(", "),
      tires: tires.join(", "),
      hasWheels: !!wheels.length,
      hasTires: !!tires.length,
    };
  }

  function reportUnsupportedSanitizer() {
    if (hasReportedUnsupportedSanitizer) {
      return;
    }

    hasReportedUnsupportedSanitizer = true;

    if (typeof console !== "undefined" && console.error) {
      console.error(
        "CCAProductGallery omitted accessory descriptions because DOMPurify is unavailable or unsupported."
      );
    }
  }

  function sanitizeAccessoryDescription(description) {
    if (
      !DOMPurify ||
      DOMPurify.isSupported !== true ||
      !_.isFunction(DOMPurify.sanitize)
    ) {
      reportUnsupportedSanitizer();
      return "";
    }

    try {
      return DOMPurify.sanitize(
        String(description || ""),
        ACCESSORY_DESCRIPTION_SANITIZE_CONFIG
      );
    } catch (error) {
      reportUnsupportedSanitizer();
      return "";
    }
  }

  function getDescriptionDeduplicationKey(sanitizedDescription) {
    return jQuery("<div></div>")
      .html(sanitizedDescription)
      .text()
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  function toTitleCase(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/(^|[\s/&-])([a-z])/g, function (match, separator, letter) {
        return separator + letter.toUpperCase();
      });
  }

  function getComponentInfoTitle(component) {
    var classFullName = component.classfullname || "";
    var rootClassName = classFullName.split(" : ")[0];
    var title = toTitleCase(rootClassName);

    return title ? title + " Info" : "Product Info";
  }

  function getAccessoryDescriptionBlocks(components) {
    var blocks = [];
    var seenDescriptions = Object.create(null);

    _.each(components || [], function (component) {
      var sanitizedDescription;
      var deduplicationKey;

      if (
        !component ||
        !component.storedescription ||
        SchemaHelper.isWheelOrTireComponent(component)
      ) {
        return;
      }

      sanitizedDescription = sanitizeAccessoryDescription(
        component.storedescription
      );
      deduplicationKey = getDescriptionDeduplicationKey(sanitizedDescription);

      if (!deduplicationKey || seenDescriptions[deduplicationKey]) {
        return;
      }

      seenDescriptions[deduplicationKey] = true;
      blocks.push({
        title: getComponentInfoTitle(component),
        itemname: component.itemname || "",
        brand: component.brand || "",
        classfullname: component.classfullname || "",
        descriptionHtml: sanitizedDescription,
      });
    });

    return blocks;
  }

  return {
    compactParts: compactParts,
    parseDescription: parseDescription,
    formatWheelCaption: formatWheelCaption,
    formatTireCaption: formatTireCaption,
    formatGalleryDescription: formatGalleryDescription,
    formatWheelProductName: formatWheelProductName,
    formatTireProductName: formatTireProductName,
    getSeoProductSummary: getSeoProductSummary,
    getComponentInfoTitle: getComponentInfoTitle,
    sanitizeAccessoryDescription: sanitizeAccessoryDescription,
    getDescriptionDeduplicationKey: getDescriptionDeduplicationKey,
    getAccessoryDescriptionBlocks: getAccessoryDescriptionBlocks,
  };
});
